<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'
import {
  InspirationImagesDocument,
  type InspirationImage,
} from '~/graphql/operations'
import { TagSelect } from '~/shared/inputs/tag.input'
import { ProductExtSelect } from '~/shared/inputs/product-ext.input'
import { SearchText } from '~/shared/inputs'

const props = defineProps<{
  context: FormKitFrameworkContext
}>()

const {
  state: filterState,
  schema: filterSchema,
  get,
  clearState,
} = useFiltersState([
  new SearchText({
    name: 'search',
    label: 'Search by name',
    placeholder: 'Enter name...',
    classes: { outer: 'flex-1' },
  }).filter(),
  new TagSelect({
    name: 'tagId',
    label: 'Filter by Tag',
    placeholder: 'Select tag...',
    mode: 'tags',
    classes: { outer: 'flex-1' },
  }).filter(),
  new ProductExtSelect({
    name: 'productId',
    label: 'Filter by Linked Product',
    placeholder: 'Select product...',
    mode: 'tags',
    classes: { outer: 'flex-1' },
    where: () => ({ channelId: 1 }),
  }).filter(),
])

const selectedImageIds = ref<number[]>(props.context._value || [])

watch(
  () => props.context._value,
  (newVal) => {
    if (newVal && Array.isArray(newVal)) {
      selectedImageIds.value = [...newVal]
    }
  },
  { immediate: true },
)

const updateValue = () => {
  props.context.node.input(selectedImageIds.value)
  refetchSelected()
}

const queryVariables = ref({
  where: {
    publishStatus: { equals: 'published' },
  },
  page: 1,
  perPage: 300,
})

const updateQueryVariables = () => {
  const searchValue = get<string>('search').asContains()
  const tagIdsValue = get('tagId', (t: any) => +t).asNumberIn()
  const productIdsValue = get('productId', (p: any) => +p).asNumberIn()

  queryVariables.value = {
    where: {
      publishStatus: showDraftImages.value
        ? undefined
        : { equals: 'published' },
      name: searchValue || undefined,
      tagIds: tagIdsValue || undefined,
      productIds: productIdsValue || undefined,
    } as any,
    page: 1,
    perPage: 300,
  }
}

const { data, fetching, error, executeQuery } = useQuery({
  query: InspirationImagesDocument,
  variables: queryVariables,
})

const selectedQueryVariables = computed(() => {
  if (selectedImageIds.value.length === 0) return null
  return {
    where: { id: { in: selectedImageIds.value } },
    page: 1,
    perPage: selectedImageIds.value.length,
  }
})

const { data: selectedData, executeQuery: executeSelectedQuery } = useQuery({
  query: InspirationImagesDocument,
  variables: selectedQueryVariables,
  pause: computed(() => selectedImageIds.value.length === 0),
})

const refetchSelected = () => {
  if (selectedImageIds.value.length > 0) {
    executeSelectedQuery({ requestPolicy: 'network-only' })
  }
}

const debouncedExecute = useDebounceFn(() => {
  updateQueryVariables()
  executeQuery({ requestPolicy: 'network-only' })
}, 300)

watch(
  filterState,
  () => {
    debouncedExecute()
  },
  { deep: true },
)

const selectedImages = computed(() => {
  return selectedData.value?.inspirationImages?.data || []
})

const inspirationImages = computed(() => {
  const filtered = data.value?.inspirationImages?.data || []
  const selected = selectedImages.value
  if (selected.length === 0) return filtered

  const selectedIdSet = new Set(selected.map((img: any) => img.id))
  const rest = filtered.filter((img: any) => !selectedIdSet.has(img.id))
  return [...selected, ...rest]
})

const totalCount = computed(() => {
  return data.value?.inspirationImages?.meta?.total || 0
})

// Selection handling
const isSelected = (imageId: number) => {
  return selectedImageIds.value.includes(imageId)
}

const toggleSelection = (imageId: number) => {
  const index = selectedImageIds.value.indexOf(imageId)
  if (index > -1) {
    selectedImageIds.value.splice(index, 1)
  } else {
    selectedImageIds.value.push(imageId)
  }
  updateValue()
}

const selectAll = () => {
  const allIds = inspirationImages.value.map((img: any) => img.id)
  const newIds = allIds.filter(
    (id: number) => !selectedImageIds.value.includes(id),
  )
  selectedImageIds.value.push(...newIds)
  updateValue()
}

const deselectAll = () => {
  const visibleIds = inspirationImages.value.map((img: any) => img.id)
  selectedImageIds.value = selectedImageIds.value.filter(
    (id) => !visibleIds.includes(id),
  )
  updateValue()
}

const clearFiltersHandler = () => {
  clearState()
}

const showOnlySelected = ref(false)
const showDraftImages = ref(false)

watch(showDraftImages, () => {
  updateQueryVariables()
  executeQuery({ requestPolicy: 'network-only' })
})

const displayedImages = computed(() => {
  const images = inspirationImages.value
  if (showOnlySelected.value) {
    return images.filter((img: any) => selectedImageIds.value.includes(img.id))
  }
  return images
})
</script>

<template>
  <div class="border border-gray-200 rounded-lg overflow-hidden">
    <div class="bg-gray-50 p-4 border-b border-gray-200">
      <h4 class="text-sm font-medium text-gray-700 mb-3">
        Filter Inspiration Images
      </h4>
      <FormKit type="form" :actions="false" v-model="filterState">
        <div class="flex flex-row gap-4">
          <FormKitSchema :schema="filterSchema" />
        </div>
      </FormKit>
      <div class="mt-3 flex justify-end">
        <button
          type="button"
          class="text-xs text-gray-500 hover:text-gray-700 underline"
          @click="clearFiltersHandler"
        >
          Clear filters
        </button>
      </div>
    </div>

    <div class="p-4 bg-white">
      <div class="flex gap-2 mb-3 items-center justify-end">
        <button
          type="button"
          class="text-xs px-2 py-1 rounded transition-colors"
          :class="
            showDraftImages
              ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          "
          @click="showDraftImages = !showDraftImages"
        >
          Show draft images
        </button>
        <span class="w-px h-4 bg-gray-200" />
        <button
          type="button"
          class="text-xs px-2 py-1 rounded transition-colors"
          :class="
            showOnlySelected
              ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          "
          :disabled="selectedImageIds.length === 0"
          @click="showOnlySelected = !showOnlySelected"
        >
          Show only selected
        </button>
        <span class="w-px h-4 bg-gray-200" />
        <button
          type="button"
          class="text-xs px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          @click="selectAll"
          :disabled="fetching || inspirationImages.length === 0"
        >
          Select all visible
        </button>
        <button
          type="button"
          class="text-xs px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          @click="deselectAll"
          :disabled="selectedImageIds.length === 0"
        >
          Deselect all visible
        </button>
      </div>
      <div
        class="flex justify-between items-center mb-3 pb-3 border-b border-gray-100"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-600">
            {{ totalCount }} image{{ totalCount !== 1 ? 's' : '' }} found
          </span>
          <span
            v-if="selectedImageIds.length > 0"
            class="text-sm font-medium text-primary-600"
          >
            ({{ selectedImageIds.length }} selected)
          </span>
        </div>
      </div>

      <div v-if="fetching" class="text-center py-12">
        <Spinner class="w-6 h-6 mx-auto text-gray-400" />
        <p class="text-sm text-gray-400 mt-2">Loading images...</p>
      </div>

      <div v-else-if="error" class="text-center py-12">
        <div
          class="i-heroicons-exclamation-triangle w-8 h-8 text-red-400 mx-auto mb-2"
        />
        <p class="text-sm text-red-500">Failed to load images</p>
      </div>

      <div v-else-if="displayedImages.length === 0" class="text-center py-12">
        <div class="i-heroicons-photo w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p class="text-sm text-gray-400">
          {{
            showOnlySelected
              ? 'No selected images'
              : 'No inspiration images found'
          }}
        </p>
      </div>

      <div
        v-else
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-96 overflow-y-auto"
      >
        <div
          v-for="image in displayedImages as InspirationImage[]"
          :key="image.id"
          class="relative border rounded-lg overflow-hidden cursor-pointer transition-all hover:border-gray-300 hover:shadow-sm"
          :class="
            isSelected(image.id!)
              ? 'border-primary-500 ring-2 ring-primary-200'
              : 'border-gray-200'
          "
          @click="toggleSelection(image.id!)"
        >
          <div class="absolute top-2 left-2 z-10">
            <input
              type="checkbox"
              :checked="isSelected(image.id!)"
              @click.stop="toggleSelection(image.id!)"
              class="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
            />
          </div>
          <div class="aspect-square bg-gray-100">
            <img
              v-if="image.image?.fileUrl"
              :src="image.image.fileUrl"
              :alt="`Image #${image.id}`"
              class="w-full h-full object-cover"
            />
            <div v-else class="w-full h-full flex items-center justify-center">
              <div class="i-heroicons-photo w-8 h-8 text-gray-300" />
            </div>
          </div>
          <div class="p-2 bg-white border-t border-gray-100">
            <div class="flex items-center gap-1 mb-1">
              <Badge
                :type="
                  image.publishStatus === 'published' ? 'success' : 'attention'
                "
                class="text-xs"
              >
                {{ image.publishStatus }}
              </Badge>
              <Badge
                v-if="image.favorite"
                type="success"
                :check="true"
                class="text-xs"
              />
            </div>
            <span class="block text-xs text-gray-400">ID: {{ image.id }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="filterState.productId"
        class="mt-4 pt-3 border-t border-gray-100 flex items-center"
      >
        <div
          class="i-heroicons-information-circle w-4 h-4 text-blue-400 inline-block mr-1"
        />
        <span class="text-xs text-blue-600">
          Product filter will be applied when the module is rendered.
        </span>
      </div>
    </div>
  </div>
</template>
